import QRCode from 'qrcode';
import type { 
  QRCodeConfig, 
  QRCodeDownloadOptions, 
  QRCodeScanEvent 
} from '@/types';
import { trackMenuView } from './analytics';

// Size configurations for different QR code sizes
export const QR_SIZE_CONFIGS = {
  small: { 
    pixels: 256, 
    margin: 4, 
    scale: 8,
    description: 'Small (256x256px) - Business cards, receipts'
  },
  medium: { 
    pixels: 512, 
    margin: 4, 
    scale: 16,
    description: 'Medium (512x512px) - Table tents, flyers'
  },
  large: { 
    pixels: 1024, 
    margin: 4, 
    scale: 32,
    description: 'Large (1024x1024px) - Posters, A4 prints'
  },
  'extra-large': { 
    pixels: 2048, 
    margin: 4, 
    scale: 64,
    description: 'Extra Large (2048x2048px) - Banners, large displays'
  }
} as const;

/**
 * Generate QR code as data URL (base64)
 */
export const generateQRCode = async (config: QRCodeConfig): Promise<string> => {
  try {
    const sizeConfig = QR_SIZE_CONFIGS[config.size];
    
    const options = {
      errorCorrectionLevel: 'M' as const, // Use medium error correction by default
      type: 'image/png' as const,
      margin: sizeConfig.margin,
      scale: sizeConfig.scale,
      width: sizeConfig.pixels,
      color: {
        dark: config.foregroundColor,
        light: config.backgroundColor,
      },
    };

    const dataUrl = await QRCode.toDataURL(config.url, options);
    return dataUrl;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Generate QR code as SVG string
 */
export const generateQRCodeSVG = async (config: QRCodeConfig): Promise<string> => {
  try {
    const sizeConfig = QR_SIZE_CONFIGS[config.size];
    
    const options = {
      errorCorrectionLevel: 'M' as const, // Use medium error correction by default
      type: 'svg' as const,
      margin: sizeConfig.margin,
      scale: sizeConfig.scale,
      width: sizeConfig.pixels,
      color: {
        dark: config.foregroundColor,
        light: config.backgroundColor,
      },
    };

    const svg = await QRCode.toString(config.url, options);
    return svg;
  } catch (error) {
    console.error('Error generating QR code SVG:', error);
    throw new Error('Failed to generate QR code SVG');
  }
};

/**
 * Download QR code in specified format
 */
export const downloadQRCode = async (
  config: QRCodeConfig,
  options: QRCodeDownloadOptions,
  restaurantName: string
): Promise<void> => {
  try {
    const timestamp = new Date().toISOString().split('T')[0];
    const baseFileName = `${restaurantName.replace(/[^a-zA-Z0-9]/g, '_')}_QR_${config.size}_${timestamp}`;

    if (options.format === 'png') {
      const dataUrl = await generateQRCode(config);
      downloadDataUrl(dataUrl, `${baseFileName}.png`);
    } else if (options.format === 'svg') {
      const svg = await generateQRCodeSVG(config);
      const blob = new window.Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      downloadUrl(url, `${baseFileName}.svg`);
      URL.revokeObjectURL(url);
    } else if (options.format === 'pdf') {
      // For PDF, we'll generate a high-quality PNG and embed it in a PDF-like structure
      const dataUrl = await generateQRCode(config);
      await downloadQRCodeAsPDF(dataUrl, `${baseFileName}.pdf`, config, restaurantName);
    }
  } catch (error) {
    console.error('Error downloading QR code:', error);
    throw new Error(`Failed to download QR code as ${options.format.toUpperCase()}`);
  }
};

/**
 * Helper function to download data URL as file
 */
const downloadDataUrl = (dataUrl: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Helper function to download URL as file
 */
const downloadUrl = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Create a simple PDF-like structure with QR code
 * This creates an HTML canvas, draws the QR code with some text, then downloads as image
 */
const downloadQRCodeAsPDF = async (
  qrDataUrl: string,
  filename: string,
  config: QRCodeConfig,
  restaurantName: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    // Set canvas size for PDF-like layout (A4 proportions)
    canvas.width = 2480; // 300 DPI for A4 width
    canvas.height = 3508; // 300 DPI for A4 height
    
    // Fill white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const img = new Image();
    img.onload = () => {
      try {
        // Calculate QR code size and position (centered)
        const sizeConfig = QR_SIZE_CONFIGS[config.size];
        const qrSize = Math.min(1200, sizeConfig.pixels * 2); // Scale up for PDF
        const qrX = (canvas.width - qrSize) / 2;
        const qrY = (canvas.height - qrSize) / 2 - 200; // Offset up to make room for text
        
        // Draw QR code
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        
        // Add restaurant name above QR code
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 60px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(restaurantName, canvas.width / 2, qrY - 100);
        
        // Add URL below QR code
        ctx.font = '32px Arial, sans-serif';
        ctx.fillText(config.url, canvas.width / 2, qrY + qrSize + 80);
        
        // Add instructions below URL
        ctx.font = '24px Arial, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.fillText('Scan with your phone camera to view menu', canvas.width / 2, qrY + qrSize + 130);
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            downloadUrl(url, filename.replace('.pdf', '.png'));
            URL.revokeObjectURL(url);
            resolve();
          } else {
            reject(new Error('Failed to create PDF'));
          }
        }, 'image/png', 1.0);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load QR code image'));
    img.src = qrDataUrl;
  });
};

/**
 * Create default QR code configuration
 */
export const createDefaultQRConfig = (restaurantId: string): QRCodeConfig => {
  const baseUrl = window.location.origin;
  return {
    restaurantId,
    url: `${baseUrl}/menu/${restaurantId}`,
    size: 'medium',
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    includeRestaurantLogo: false,
  };
};

/**
 * Validate QR code configuration
 */
export const validateQRConfig = (config: QRCodeConfig): string[] => {
  const errors: string[] = [];
  
  if (!config.restaurantId) {
    errors.push('Restaurant ID is required');
  }
  
  if (!config.url || !isValidUrl(config.url)) {
    errors.push('Valid URL is required');
  }
  
  if (!Object.keys(QR_SIZE_CONFIGS).includes(config.size)) {
    errors.push('Invalid size selection');
  }
  
  if (!isValidHexColor(config.foregroundColor)) {
    errors.push('Invalid foreground color');
  }
  
  if (!isValidHexColor(config.backgroundColor)) {
    errors.push('Invalid background color');
  }
  
  if (config.foregroundColor === config.backgroundColor) {
    errors.push('Foreground and background colors must be different');
  }
  
  return errors;
};

/**
 * Helper function to validate hex color
 */
const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

/**
 * Helper function to validate URL
 */
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Track QR code scan event (integrated with existing analytics)
 */
export const trackQRCodeScan = async (event: QRCodeScanEvent): Promise<void> => {
  try {
    // Use existing analytics service to track QR code scans
    await trackMenuView(event.restaurantId, {
      userAgent: event.userAgent,
      referrer: 'qr-code-scan', // Mark as QR code scan
    });
    
    console.log('QR Code scan tracked:', event);
  } catch (error) {
    console.error('Error tracking QR code scan:', error);
    // Don't throw error - analytics shouldn't break the app
  }
};
